
import React, { useState, useEffect } from "react";
import ContentDisplay from "@/components/dashboard/ContentDisplay";
import { useRelatedContent } from "@/hooks/related-content/useRelatedContent";
import RelatedContentHeader from "./RelatedContentHeader";
import { ViewMode } from "@/hooks/usePersistentViewMode";

interface ContentRelatedItemsProps {
  collectionId?: string;
  currentContentId: string;
  viewMode: ViewMode;
  onContentClick: (id: string) => void;
  onDeleteContent: (id: string) => void;
  onArchiveContent: (id: string) => void;
  collectionColor?: string | null; // Add this prop to receive color from parent
}

const ContentRelatedItems = ({
  collectionId,
  currentContentId,
  viewMode,
  onContentClick,
  onDeleteContent,
  onArchiveContent,
  collectionColor: parentProvidedColor // Receive color from parent
}: ContentRelatedItemsProps) => {
  const [isNavigating, setIsNavigating] = useState(false);
  const {
    contentItems,
    isLoading,
    collectionColor: fetchedCollectionColor,
    collectionName
  } = useRelatedContent({
    collectionId,
    currentContentId,
    sourceType: "content"
  });

  // Always use parent-provided color first (which comes from the focused content item)
  // This ensures consistency between initial load and navigation
  const effectiveCollectionColor = parentProvidedColor || fetchedCollectionColor || "#6B7789";

  // Reset navigation state when current content changes
  useEffect(() => {
    setIsNavigating(false);
  }, [currentContentId]);

  // Log color information to help debug
  useEffect(() => {
    console.log(`[COLOR DEBUG] ContentRelatedItems - Collection colors:`, {
      parentProvidedColor, 
      fetchedCollectionColor,
      effectiveCollectionColor,
      itemsCount: contentItems.length,
      collectionId: collectionId
    });
    if (contentItems.length > 0) {
      console.log(`[COLOR DEBUG] First related item color: ${contentItems[0].collection_color}, using: ${effectiveCollectionColor}`);
    }
  }, [parentProvidedColor, fetchedCollectionColor, contentItems, effectiveCollectionColor]);

  // Create a safer click handler that prevents double clicks
  const handleItemClick = (id: string) => {
    // Ignore clicks if already navigating or if clicking the current item
    if (isNavigating || id === currentContentId) {
      return;
    }
    
    // Immediately set navigating state to prevent multiple clicks
    setIsNavigating(true);
    
    // Call the passed click handler
    onContentClick(id);
    
    // Add a failsafe that resets the navigation state after a timeout
    setTimeout(() => {
      if (isNavigating) {
        setIsNavigating(false);
      }
    }, 5000); // 5 second timeout
  };

  // Add collection color to all content items to ensure consistency
  const contentItemsWithColor = contentItems.map(item => ({
    ...item,
    collection_color: effectiveCollectionColor
  }));

  return (
    <div className="space-y-4">
      <RelatedContentHeader 
        collectionColor={effectiveCollectionColor} 
        collectionName={collectionName} 
      />
      
      {contentItemsWithColor.length > 0 ? (
        <ContentDisplay
          contentItems={contentItemsWithColor}
          viewMode={viewMode}
          onContentClick={handleItemClick}
          onDeleteContent={onDeleteContent}
          onArchiveContent={onArchiveContent}
          collectionColor={effectiveCollectionColor} // Use the effective color
        />
      ) : (
        <div className="p-4 text-center text-gray-500 border border-dashed rounded-md">
          {isLoading ? (
            <p>Loading related content...</p>
          ) : (
            <p>No related content found</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentRelatedItems;
